<template>
  <Is24Layout
    :baseUrl="baseUrl"
    :currentLanguage="locale"
    :currentRoute="route"
    :getLanguageToLink="getLanguageSwitchLink"
    :isWhiteLabelSolution="true"
  >
    <div class="layout-container">
      <router-view />
      <div class="powered-by">
        <span>powered by</span>
        <a href="/"><LogoIs24 class="logo" /></a>
      </div>
    </div>
  </Is24Layout>
</template>

<script lang="ts">
import { useLanguageSwitchLink } from '@/composables';
import { getSSRContext } from '@/helpers/SSR/ssr';
import { HgSupportedLanguage, Is24Layout, LogoIs24 } from '@smg-real-estate/domus';
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import {
  getLayoutSolutionAgencyConfiguration,
  getLayoutSolutionRequest,
} from '@/resultlist/layoutSolutionRequest';
import { setGeoData } from '@smg-real-estate/geo-libs';
import { getBasicPageViewTrackingContext } from './helpers/GTM/GTMHelpers';
import { GTMPageType } from './helpers/GTM/GTMParameters';
import initIframeResizer from './helpers/initIframeResizer';
import { normalizedFullPath } from './helpers/RouterHelpers';

export default defineComponent({
  name: 'AppIs24',
  components: {
    Is24Layout,
    LogoIs24,
  },
  setup() {
    const route = useRoute();
    const { locale } = useI18n();
    const baseUrl = import.meta.env.VUE_PLATFORM_BASE_URL;
    const { getLanguageSwitchLink } = useLanguageSwitchLink();

    return {
      route,
      locale,
      baseUrl,
      getLanguageSwitchLink,
    };
  },
  beforeMount() {
    initIframeResizer();
  },
  async serverPrefetch() {
    const { geoData, layoutSolutionAgencyConfigurations } = getSSRContext();
    // Set geo-data from layer added by the lambda
    setGeoData(geoData);
    // Set layout solution agency configuration from the lambda
    const layoutSolutionRequest = getLayoutSolutionRequest(this.$route.query);
    if (layoutSolutionRequest) {
      const agency = layoutSolutionRequest.agency;
      // For multiple agencies (comma-separated), don't use configuration
      const configuration = agency.includes(',')
        ? undefined
        : getLayoutSolutionAgencyConfiguration(layoutSolutionAgencyConfigurations, agency);
      this.$store.commit('layoutSolution/setAgencyConfiguration', configuration);
    }
    getSSRContext().pageTrackingContext = JSON.stringify(
      getBasicPageViewTrackingContext(
        this.$route.meta.gtmPageType as GTMPageType,
        this.$i18n.locale as HgSupportedLanguage,
        this.$route.fullPath,
        normalizedFullPath(this.$route, this.$router) ?? null,
      ),
    );
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/variables';

.powered-by {
  display: flex;
  flex-wrap: wrap;
  gap: $space-50;
  align-items: center;
  justify-content: end;

  span {
    @include caption;
  }

  a {
    .logo {
      display: inline-block;
      width: $space-800;
    }
  }
}
</style>
